import axios from "axios";
import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useHiddenMode } from "../contexts/HiddenModeContext";
import "../css/historicalUpload.css";
import { Project, projectService } from "../services/projectService";
import { getToken } from "../utils/authStorage";

interface UploadResult {
  success: boolean;
  message: string;
  data?: {
    projectName: string;
    uploadedBy: string;
    uploadedAt: string;
    tabs: {
      cancelUnits?: {
        success: number;
        failed: number;
        errors: string[];
        bookings: Array<{
          bookingId: number;
          unit: string;
          customer: string;
          date: string;
        }>;
      };
      receivableIncome?: {
        success: number;
        failed: number;
        errors: string[];
        incomes: Array<{
          id: string;
          bookingId: number;
          unit: string;
          customer: string;
          amount: number;
          date: string;
        }>;
        refunds: Array<{
          id: string;
          bookingId: number;
          unit: string;
          customer: string;
          amount: number;
          date: string;
        }>;
      };
      cancelIncome?: {
        success: number;
        failed: number;
        errors: string[];
        incomes: Array<{
          id: number;
          bookingId: number;
          unit: string;
          customer: string;
          amount: number;
          date: string;
        }>;
        refunds: Array<{
          id: number;
          bookingId: number;
          unit: string;
          customer: string;
          amount: number;
          date: string;
        }>;
      };
    };
  };
}

const HistoricalUpload: React.FC = () => {
  const { user } = useAuth();
  const { isHiddenModeActive } = useHiddenMode();
  const [activeTab, setActiveTab] = useState<
    "cancelUnits" | "receivableIncome" | "cancelIncome"
  >("cancelUnits");
  const [file, setFile] = useState<File | null>(null);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check if user is superadmin
  const isSuperAdmin =
    user?.allPermissions?.includes("*") ||
    user?.allPermissions?.includes("superadmin");

  // Load projects on mont
  useEffect(() => {
    loadProjects();
  }, []);

  // Redirect if not in hidden mode (unless superadmin)
  if (!isHiddenModeActive && !isSuperAdmin) {
    return (
      <div className="unauthorized-access">
        <div className="unauthorized-content">
          <h2>🔒 Access Denied</h2>
          <p>This page requires hidden mode to be active.</p>
          <p>
            Please activate hidden mode using <code>Ctrl+Shift+6</code> or{" "}
            <code>Ctrl+Shift+H</code>
          </p>
        </div>
      </div>
    );
  }

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const projectsData = await projectService.getProjects(true);
      setProjects(projectsData);

      if (projectsData.length > 0) {
        setSelectedProject(projectsData[0].id.toString());
      }
    } catch (error) {
      console.error("Error loading projects:", error);
      setError("Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Check file type
      const fileExtension = selectedFile.name.split(".").pop()?.toLowerCase();
      if (!["csv", "xlsx", "xls"].includes(fileExtension || "")) {
        setError("Only CSV and Excel files are allowed (.csv, .xlsx, .xls)");
        setFile(null);
        return;
      }

      // Check file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        setFile(null);
        return;
      }

      setFile(selectedFile);
      setError(null);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    console.log("\n═══════════════════════════════════════════════════════");
    console.log("🚀 STARTING HISTORICAL UPLOAD");
    console.log("═══════════════════════════════════════════════════════\n");

    if (!file) {
      console.error("❌ No file selected");
      setError("Please select a file to upload");
      return;
    }

    if (!selectedProject) {
      console.error("❌ No project selected");
      setError("Please select a project");
      return;
    }

    console.log("📋 Upload details:", {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      projectId: selectedProject,
    });

    setLoading(true);
    setError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", selectedProject);

      console.log("📤 FormData prepared:", {
        file: file.name,
        projectId: selectedProject,
      });

      const token = getToken();
      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5010/api";

      console.log("🔑 Using token:", token ? "Token found" : "No token");
      console.log("🌐 API URL:", `${API_URL}/historical-upload`);

      console.log("📡 Sending request to backend...");
      const response = await axios.post<UploadResult>(
        `${API_URL}/historical-upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("✅ Response received:", response.data);
      setUploadResult(response.data);

      console.log("📊 Upload results:", {
        success: response.data.success,
        message: response.data.message,
        cancelUnitsSuccess: response.data.data?.tabs?.cancelUnits?.success,
        cancelUnitsFailed: response.data.data?.tabs?.cancelUnits?.failed,
        errors: response.data.data?.tabs?.cancelUnits?.errors?.length || 0,
      });

      // Clear file input after successful upload
      setFile(null);
      const fileInput = document.getElementById(
        "file-input"
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";

      console.log("✅ Upload complete!\n");
    } catch (error: any) {
      console.error("❌ Upload error:", error);
      console.error("❌ Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setError(
        error.response?.data?.message ||
          error.message ||
          "Failed to upload file"
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadSampleCSV = () => {
    const csvContent = `No,Date,Unit No,Name
25,2/4/2024,205-Cancel,Rameshbhai Natvarbhai
11,8/19/2023,2-Cancel,JP Jewalars
16,12/5/2023,B-406-Cancel,Rajeshkumar
17,12/5/2023,407-Cancel,Bhanuben
14,10/18/2023,504-Cancel,Patel Bipinbhai`;

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample-cancel-units.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadReceivableSampleCSV = () => {
    const csvContent = `No,Date,Unit No,Name,Cr
25,2/4/2024,205-Cancel,Rameshbhai Natvarbhai,₹ 50.00
11,8/19/2023,2-Cancel,JP Jewalars,₹ 5000.00
12,9/1/2023,2-Cancel,JP Jewalars,₹ 5000.00
14,10/18/2023,504-Cancel,Patel Bipinbhai,₹ 1000.00
16,12/5/2023,406-Cancel,Rajeshkumar,-₹ 100.00`;

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample-receivable-income.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadCancelIncomeSampleCSV = () => {
    const csvContent = `Date,Unit No,Name,Amount,Mode,Bank Name,Chq No
11/17/2025,B-104-Cancel,Urmilaben Rameshkumar Joshi,94900,MB,,998579448381
2/6/2024,E-1001-Cancel,Dr Sureshbhai Jaykishan Binoi,250000,Cheque,ICICI Bank,1205
2/26/2024,G-401-Cancel,Pareshbhai Hargovindbhai Patel,50000,IMPS,,405714177340
7/8/2024,F-201-Cancel,Laxman M Patni,100000,Cheque,Post,861810
3/15/2024,G-501-Cancel,Rameshbhai Patel,-25000,Cash,,`;

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample-cancel-income.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleReceivableUpload = async () => {
    console.log("\n═══════════════════════════════════════════════════════");
    console.log("🚀 STARTING RECEIVABLE INCOME UPLOAD");
    console.log("═══════════════════════════════════════════════════════\n");

    if (!file) {
      console.error("❌ No file selected");
      setError("Please select a file to upload");
      return;
    }

    if (!selectedProject) {
      console.error("❌ No project selected");
      setError("Please select a project");
      return;
    }

    console.log("📋 Upload details:", {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      projectId: selectedProject,
    });

    setLoading(true);
    setError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", selectedProject);
      formData.append("uploadType", "receivable");

      console.log("📤 FormData prepared for receivable income upload");

      const token = getToken();
      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5010/api";

      console.log("📡 Sending request to backend...");
      const response = await axios.post(
        `${API_URL}/historical-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("✅ Upload response received:", response.data);
      setUploadResult(response.data);
      setFile(null);
    } catch (err: any) {
      console.error("❌ Upload failed:", err);
      const errorMessage =
        err.response?.data?.message || err.message || "Upload failed";
      setError(errorMessage);
      setUploadResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelIncomeUpload = async () => {
    console.log("\n═══════════════════════════════════════════════════════");
    console.log("🚀 STARTING CANCEL INCOME UPLOAD");
    console.log("═══════════════════════════════════════════════════════\n");

    if (!file) {
      console.error("❌ No file selected");
      setError("Please select a file to upload");
      return;
    }

    if (!selectedProject) {
      console.error("❌ No project selected");
      setError("Please select a project");
      return;
    }

    console.log("📋 Upload details:", {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      projectId: selectedProject,
    });

    setLoading(true);
    setError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", selectedProject);
      formData.append("uploadType", "cancelIncome");

      console.log("📤 FormData prepared for cancel income upload");

      const token = getToken();
      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5010/api";

      console.log("📡 Sending request to backend...");
      const response = await axios.post(
        `${API_URL}/historical-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("✅ Upload response received:", response.data);
      setUploadResult(response.data);
      setFile(null);
    } catch (err: any) {
      console.error("❌ Upload failed:", err);
      const errorMessage =
        err.response?.data?.message || err.message || "Upload failed";
      setError(errorMessage);
      setUploadResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedProjectName = projects.find(
    (p) => p.id.toString() === selectedProject
  )?.name;

  return (
    <div className="historical-upload-container">
      <div className="page-header">
        <h1>📊 Historical Upload</h1>
        <p className="page-description">
          Upload historical data from CSV or Excel files
        </p>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === "cancelUnits" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("cancelUnits");
            setFile(null);
            setError(null);
            setUploadResult(null);
          }}
        >
          📋 Cancel Units
        </button>
        <button
          className={`tab-btn ${
            activeTab === "receivableIncome" ? "active" : ""
          }`}
          onClick={() => {
            setActiveTab("receivableIncome");
            setFile(null);
            setError(null);
            setUploadResult(null);
          }}
        >
          💰 Receivable Income
        </button>
        <button
          className={`tab-btn ${activeTab === "cancelIncome" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("cancelIncome");
            setFile(null);
            setError(null);
            setUploadResult(null);
          }}
        >
          💸 Cancel Income
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "cancelUnits" && (
        <>
          {/* Instructions Card */}
          <div className="instructions-card">
            <h3>📋 Instructions</h3>
            <ul>
              <li>
                ✅ Upload <strong>CSV</strong> or <strong>Excel</strong> files
              </li>
              <li>
                ✅ File must contain: <code>No, Date, Unit No, Name</code>{" "}
                columns
              </li>
              <li>
                ✅ Unit numbers should have <code>-Cancel</code> suffix (e.g.,{" "}
                <code>205-Cancel</code>, <code>B-302-Cancel</code>)
              </li>
              <li>
                ✅ All bookings will be marked as <strong>cancelled</strong>{" "}
                with zero amounts
              </li>
              <li>
                ✅ Units will become <strong>unsold</strong> (available for new
                bookings)
              </li>
              <li>
                ✅ Maximum file size: <strong>10MB</strong>
              </li>
            </ul>
            <button className="download-sample-btn" onClick={downloadSampleCSV}>
              📥 Download Sample CSV
            </button>
          </div>

          {/* Upload Form */}
          <div className="upload-form-card">
            <h3>📤 Upload File</h3>

            {/* Project Selection */}
            <div className="form-group">
              <label htmlFor="project-select">Select Project *</label>
              {loadingProjects ? (
                <p>Loading projects...</p>
              ) : (
                <select
                  id="project-select"
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  disabled={loading}
                >
                  <option value="">-- Select Project --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* File Input */}
            <div className="form-group">
              <label htmlFor="file-input">Select File (CSV or Excel) *</label>
              <input
                id="file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                disabled={loading}
              />
              {file && (
                <div className="file-info">
                  <p>
                    📄 <strong>{file.name}</strong>
                  </p>
                  <p>Size: {(file.size / 1024).toFixed(2)} KB</p>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <strong>❌ Error:</strong> {error}
              </div>
            )}

            {/* Upload Button */}
            <button
              className="upload-btn"
              onClick={handleUpload}
              disabled={loading || !file || !selectedProject}
            >
              {loading ? "⏳ Uploading..." : "📤 Upload Historical Data"}
            </button>
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div
              className={`result-card ${
                uploadResult.success ? "success" : "error"
              }`}
            >
              <h3>
                {uploadResult.success
                  ? "✅ Upload Successful"
                  : "❌ Upload Failed"}
              </h3>
              <p>{uploadResult.message}</p>

              {uploadResult.data && (
                <>
                  <div className="result-summary">
                    <div className="summary-item">
                      <strong>Project:</strong> {uploadResult.data.projectName}
                    </div>
                    <div className="summary-item">
                      <strong>Uploaded By:</strong>{" "}
                      {uploadResult.data.uploadedBy}
                    </div>
                    <div className="summary-item">
                      <strong>Uploaded At:</strong>{" "}
                      {new Date(uploadResult.data.uploadedAt).toLocaleString()}
                    </div>
                  </div>

                  {uploadResult.data.tabs.cancelUnits && (
                    <div className="cancel-units-result">
                      <h4>📋 Cancelled Units</h4>
                      <div className="stats">
                        <div className="stat-item success">
                          <strong>✅ Success:</strong>{" "}
                          {uploadResult.data.tabs.cancelUnits.success}
                        </div>
                        <div className="stat-item failed">
                          <strong>❌ Failed:</strong>{" "}
                          {uploadResult.data.tabs.cancelUnits.failed}
                        </div>
                      </div>

                      {/* Errors */}
                      {uploadResult.data.tabs.cancelUnits.errors.length > 0 && (
                        <div className="errors-section">
                          <h5>⚠️ Errors:</h5>
                          <ul>
                            {uploadResult.data.tabs.cancelUnits.errors.map(
                              (error, index) => (
                                <li key={index}>{error}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}

                      {/* Created Bookings */}
                      {uploadResult.data.tabs.cancelUnits.bookings.length >
                        0 && (
                        <div className="bookings-section">
                          <h5>✅ Created Bookings:</h5>
                          <div className="bookings-table-container">
                            <table className="bookings-table">
                              <thead>
                                <tr>
                                  <th>Booking ID</th>
                                  <th>Unit</th>
                                  <th>Customer</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {uploadResult.data.tabs.cancelUnits.bookings.map(
                                  (booking, index) => (
                                    <tr key={index}>
                                      <td>{booking.bookingId}</td>
                                      <td>{booking.unit}</td>
                                      <td>{booking.customer}</td>
                                      <td>{booking.date}</td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Receivable Income Results */}
                  {uploadResult.data.tabs.receivableIncome && (
                    <div className="receivable-income-result">
                      <h4>💰 Receivable Income</h4>
                      <div className="stats">
                        <div className="stat-item success">
                          <strong>✅ Success:</strong>{" "}
                          {uploadResult.data.tabs.receivableIncome.success}
                        </div>
                        <div className="stat-item failed">
                          <strong>❌ Failed:</strong>{" "}
                          {uploadResult.data.tabs.receivableIncome.failed}
                        </div>
                        <div className="stat-item info">
                          <strong>💰 Income Entries:</strong>{" "}
                          {uploadResult.data.tabs.receivableIncome.incomes
                            ?.length || 0}
                        </div>
                        <div className="stat-item info">
                          <strong>↩️ Refund Entries:</strong>{" "}
                          {uploadResult.data.tabs.receivableIncome.refunds
                            ?.length || 0}
                        </div>
                      </div>

                      {/* Errors */}
                      {uploadResult.data.tabs.receivableIncome.errors?.length >
                        0 && (
                        <div className="errors-section">
                          <h5>⚠️ Errors:</h5>
                          <ul>
                            {uploadResult.data.tabs.receivableIncome.errors.map(
                              (error: string, index: number) => (
                                <li key={index}>{error}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}

                      {/* Created Income Entries */}
                      {uploadResult.data.tabs.receivableIncome.incomes?.length >
                        0 && (
                        <div className="incomes-section">
                          <h5>💰 Hidden Income Entries Created:</h5>
                          <div className="bookings-table-container">
                            <table className="bookings-table">
                              <thead>
                                <tr>
                                  <th>ID</th>
                                  <th>Unit</th>
                                  <th>Customer</th>
                                  <th>Amount</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {uploadResult.data.tabs.receivableIncome.incomes.map(
                                  (income: any, index: number) => (
                                    <tr key={index}>
                                      <td>{income.id.substring(0, 8)}...</td>
                                      <td>{income.unit}</td>
                                      <td>{income.customer}</td>
                                      <td style={{color: income.amount < 0 ? '#dc2626' : '#059669'}}>
                                        ₹{income.amount.toLocaleString()}
                                      </td>
                                      <td>
                                        {new Date(
                                          income.date
                                        ).toLocaleDateString()}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Created Refund Entries */}
                      {uploadResult.data.tabs.receivableIncome.refunds?.length >
                        0 && (
                        <div className="refunds-section">
                          <h5>↩️ Refund Transactions Created:</h5>
                          <div className="bookings-table-container">
                            <table className="bookings-table">
                              <thead>
                                <tr>
                                  <th>ID</th>
                                  <th>Unit</th>
                                  <th>Customer</th>
                                  <th>Amount</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {uploadResult.data.tabs.receivableIncome.refunds.map(
                                  (refund: any, index: number) => (
                                    <tr key={index}>
                                      <td>{refund.id.substring(0, 8)}...</td>
                                      <td>{refund.unit}</td>
                                      <td>{refund.customer}</td>
                                      <td style={{color: refund.amount < 0 ? '#dc2626' : '#059669'}}>
                                        -₹{refund.amount.toLocaleString()}
                                      </td>
                                      <td>
                                        {new Date(
                                          refund.date
                                        ).toLocaleDateString()}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Cancel Income Results */}
                  {uploadResult.data.tabs.cancelIncome && (
                    <div className="cancel-income-result">
                      <h4>💸 Cancel Income</h4>
                      <div className="stats">
                        <div className="stat-item success">
                          <strong>✅ Success:</strong>{" "}
                          {uploadResult.data.tabs.cancelIncome.success}
                        </div>
                        <div className="stat-item failed">
                          <strong>❌ Failed:</strong>{" "}
                          {uploadResult.data.tabs.cancelIncome.failed}
                        </div>
                        <div className="stat-item info">
                          <strong>💰 Income Records:</strong>{" "}
                          {uploadResult.data.tabs.cancelIncome.incomes
                            ?.length || 0}
                        </div>
                        <div className="stat-item info">
                          <strong>↩️ Refund Records:</strong>{" "}
                          {uploadResult.data.tabs.cancelIncome.refunds
                            ?.length || 0}
                        </div>
                      </div>

                      {/* Errors */}
                      {uploadResult.data.tabs.cancelIncome.errors?.length >
                        0 && (
                        <div className="errors-section">
                          <h5>⚠️ Errors:</h5>
                          <ul>
                            {uploadResult.data.tabs.cancelIncome.errors.map(
                              (error: string, index: number) => (
                                <li key={index}>{error}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}

                      {/* Created Income Records */}
                      {uploadResult.data.tabs.cancelIncome.incomes?.length >
                        0 && (
                        <div className="incomes-section">
                          <h5>💰 Income Records Created:</h5>
                          <div className="bookings-table-container">
                            <table className="bookings-table">
                              <thead>
                                <tr>
                                  <th>ID</th>
                                  <th>Unit</th>
                                  <th>Customer</th>
                                  <th>Amount</th>
                                  <th>Payment Method</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {uploadResult.data.tabs.cancelIncome.incomes.map(
                                  (income: any, index: number) => (
                                    <tr key={index}>
                                      <td>{income.id}</td>
                                      <td>{income.unit}</td>
                                      <td>{income.customer}</td>
                                      <td style={{color: income.amount < 0 ? '#dc2626' : '#059669'}}>
                                        ₹{income.amount.toLocaleString()}
                                      </td>
                                      <td>{income.paymentMethod || "N/A"}</td>
                                      <td>
                                        {new Date(
                                          income.date
                                        ).toLocaleDateString()}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Created Refund Records */}
                      {uploadResult.data.tabs.cancelIncome.refunds?.length >
                        0 && (
                        <div className="refunds-section">
                          <h5>↩️ Refund Transactions Created:</h5>
                          <div className="bookings-table-container">
                            <table className="bookings-table">
                              <thead>
                                <tr>
                                  <th>ID</th>
                                  <th>Unit</th>
                                  <th>Customer</th>
                                  <th>Amount</th>
                                  <th>Payment Method</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {uploadResult.data.tabs.cancelIncome.refunds.map(
                                  (refund: any, index: number) => (
                                    <tr key={index}>
                                      <td>{refund.id}</td>
                                      <td>{refund.unit}</td>
                                      <td>{refund.customer}</td>
                                      <td style={{color: refund.amount < 0 ? '#dc2626' : '#059669'}}>
                                        -₹{refund.amount.toLocaleString()}
                                      </td>
                                      <td>{refund.paymentMethod || "N/A"}</td>
                                      <td>
                                        {new Date(
                                          refund.date
                                        ).toLocaleDateString()}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Format Reference */}
          <div className="format-reference">
            <h3>📖 File Format Reference</h3>

            <div className="format-section">
              <h4>CSV Format:</h4>
              <pre>
                {`No,Date,Unit No,Name
25,2/4/2024,205-Cancel,Rameshbhai Natvarbhai
11,8/19/2023,2-Cancel,JP Jewalars
16,12/5/2023,B-406-Cancel,Rajeshkumar`}
              </pre>
            </div>

            <div className="format-section">
              <h4>Excel Format:</h4>
              <p>
                Sheet Name: <code>Cancel Units Details</code>
              </p>
              <table className="format-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Date</th>
                    <th>Unit No</th>
                    <th>Name</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>25</td>
                    <td>2/4/2024</td>
                    <td>205-Cancel</td>
                    <td>Rameshbhai Natvarbhai</td>
                  </tr>
                  <tr>
                    <td>11</td>
                    <td>8/19/2023</td>
                    <td>2-Cancel</td>
                    <td>JP Jewalars</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="format-notes">
              <h4>📝 Important Notes:</h4>
              <ul>
                <li>
                  <strong>Unit No:</strong> Must have <code>-Cancel</code>{" "}
                  suffix
                </li>
                <li>
                  <strong>Date:</strong> Flexible format (M/D/YYYY, DD/MM/YYYY,
                  YYYY-MM-DD)
                </li>
                <li>
                  <strong>Name:</strong> Parsed automatically (1-4 words)
                </li>
                <li>
                  <strong>Status:</strong> All bookings will be{" "}
                  <code>cancelled</code>
                </li>
                <li>
                  <strong>Amount:</strong> All amounts will be <code>₹0</code>
                </li>
                <li>
                  <strong>Units:</strong> Will be marked as <code>unsold</code>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}

      {/* Receivable Income Tab */}
      {activeTab === "receivableIncome" && (
        <div className="receivable-income-tab">
          <div className="instructions-card">
            <h3>📋 Receivable Income Upload Instructions</h3>
            <ul>
              <li>
                ✅ Upload <strong>CSV</strong> or <strong>Excel</strong> files
              </li>
              <li>
                ✅ File must contain: <code>No, Date, Unit No, Name, Cr</code>{" "}
                columns
              </li>
              <li>
                ✅ Unit numbers should have <code>-Cancel</code> suffix (e.g.,{" "}
                <code>205-Cancel</code>)
              </li>
              <li>
                ✅ <strong>Positive Cr</strong> values create Hidden Income
                entries
              </li>
              <li>
                ✅ <strong>Negative Cr</strong> values create Refund Transaction
                entries
              </li>
              <li>✅ All entries are encrypted in Receivables</li>
              <li>
                ✅ Maximum file size: <strong>10MB</strong>
              </li>
            </ul>
            <button
              className="download-sample-btn"
              onClick={downloadReceivableSampleCSV}
            >
              📥 Download Sample CSV
            </button>
          </div>

          {/* Upload Form */}
          <div className="upload-form-card">
            <h3>📤 Upload Receivable Income File</h3>

            {/* Project Selection */}
            <div className="form-group">
              <label htmlFor="project-select">Select Project *</label>
              {loadingProjects ? (
                <p>Loading projects...</p>
              ) : (
                <select
                  id="project-select"
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  disabled={loading}
                >
                  <option value="">-- Select Project --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* File Input */}
            <div className="form-group">
              <label htmlFor="file-input">Select File (CSV or Excel) *</label>
              <input
                id="file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                disabled={loading}
              />
              {file && (
                <div className="file-info">
                  <p>
                    📄 <strong>{file.name}</strong>
                  </p>
                  <p>Size: {(file.size / 1024).toFixed(2)} KB</p>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <strong>❌ Error:</strong> {error}
              </div>
            )}

            {/* Upload Button */}
            <button
              className="upload-btn"
              onClick={handleReceivableUpload}
              disabled={loading || !file || !selectedProject}
            >
              {loading ? "⏳ Uploading..." : "📤 Upload Receivable Income"}
            </button>
          </div>

          {/* Format Reference */}
          <div className="format-reference">
            <h3>📖 File Format Reference</h3>

            <div className="format-section">
              <h4>CSV Format:</h4>
              <pre>
                {`No,Date,Unit No,Name,Cr
25,2/4/2024,205-Cancel,Rameshbhai Natvarbhai,₹ 50.00
11,8/19/2023,2-Cancel,JP Jewalars,₹ 5000.00
14,10/18/2023,504-Cancel,Patel Bipinbhai,-₹ 1000.00`}
              </pre>
            </div>

            <div className="format-notes">
              <h4>📝 Important Notes:</h4>
              <ul>
                <li>
                  <strong>Unit No:</strong> Must have <code>-Cancel</code>{" "}
                  suffix
                </li>
                <li>
                  <strong>Date:</strong> Flexible format (M/D/YYYY, DD/MM/YYYY,
                  YYYY-MM-DD)
                </li>
                <li>
                  <strong>Cr (Positive):</strong> Creates Hidden Income entry
                  (₹50, ₹5000)
                </li>
                <li>
                  <strong>Cr (Negative):</strong> Creates Refund Transaction
                  (-₹1000)
                </li>
                <li>
                  <strong>Encryption:</strong> All sensitive data encrypted in
                  Receivables
                </li>
                <li>
                  <strong>Approval:</strong> Auto-approved (isApproved = true)
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Income Tab */}
      {activeTab === "cancelIncome" && (
        <div className="cancel-income-tab">
          <div className="instructions-card">
            <h3>📋 Cancel Income Upload Instructions</h3>
            <ul>
              <li>
                ✅ Upload <strong>CSV</strong> or <strong>Excel</strong> files
              </li>
              <li>
                ✅ File must contain:{" "}
                <code>
                  Date, Unit No, Name, Amount, Mode, Bank Name, Chq No
                </code>
              </li>
              <li>
                ✅ Unit numbers should have <code>-Cancel</code> suffix (e.g.,{" "}
                <code>B-104-Cancel</code>)
              </li>
              <li>
                ✅ <strong>Positive Amount</strong> creates Income record (Main
                DB)
              </li>
              <li>
                ✅ <strong>Negative Amount</strong> creates Refund Transaction
                (Main)
              </li>
              <li>✅ All entries are auto-approved (historical data)</li>
              <li>
                ✅ Maximum file size: <strong>10MB</strong>
              </li>
            </ul>
            <button
              className="download-sample-btn"
              onClick={downloadCancelIncomeSampleCSV}
            >
              📥 Download Sample CSV
            </button>
          </div>

          {/* Upload Form */}
          <div className="upload-form-card">
            <h3>📤 Upload Cancel Income File</h3>

            {/* Project Selection */}
            <div className="form-group">
              <label htmlFor="project-select">Select Project *</label>
              {loadingProjects ? (
                <p>Loading projects...</p>
              ) : (
                <select
                  id="project-select"
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  disabled={loading}
                >
                  <option value="">-- Select Project --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* File Input */}
            <div className="form-group">
              <label htmlFor="file-input">Select File (CSV or Excel) *</label>
              <input
                id="file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                disabled={loading}
              />
              {file && (
                <div className="file-info">
                  <p>
                    📄 <strong>{file.name}</strong>
                  </p>
                  <p>Size: {(file.size / 1024).toFixed(2)} KB</p>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <strong>❌ Error:</strong> {error}
              </div>
            )}

            {/* Upload Button */}
            <button
              className="upload-btn"
              onClick={handleCancelIncomeUpload}
              disabled={loading || !file || !selectedProject}
            >
              {loading ? "⏳ Uploading..." : "📤 Upload Cancel Income"}
            </button>
          </div>

          {/* Format Reference */}
          <div className="format-reference">
            <h3>📖 File Format Reference</h3>

            <div className="format-section">
              <h4>CSV Format:</h4>
              <pre>
                {`Date,Unit No,Name,Amount,Mode,Bank Name,Chq No
11/17/2025,B-104-Cancel,Urmilaben Rameshkumar Joshi,94900,MB,,998579448381
2/6/2024,E-1001-Cancel,Dr Sureshbhai Jaykishan Binoi,250000,Cheque,ICICI Bank,1205
2/26/2024,G-401-Cancel,Pareshbhai Hargovindbhai Patel,50000,IMPS,,405714177340
7/8/2024,F-201-Cancel,Laxman M Patni,-100000,Cheque,Post,861810`}
              </pre>
            </div>

            <div className="format-notes">
              <h4>📝 Important Notes:</h4>
              <ul>
                <li>
                  <strong>Unit No:</strong> Must have <code>-Cancel</code>{" "}
                  suffix
                </li>
                <li>
                  <strong>Date:</strong> Payment/Refund date (flexible format)
                </li>
                <li>
                  <strong>Amount (Positive):</strong> Creates Income record in
                  Main
                </li>
                <li>
                  <strong>Amount (Negative):</strong> Creates Refund Transaction
                  in Main
                </li>
                <li>
                  <strong>Mode:</strong> Payment method (MB, Cheque, IMPS, Cash,
                  etc.)
                </li>
                <li>
                  <strong>Bank Name:</strong> Bank name for transactions
                </li>
                <li>
                  <strong>Chq No:</strong> Cheque number or transaction
                  reference
                </li>
                <li>
                  <strong>Approval:</strong> Auto-approved (status = approved)
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricalUpload;
